import { Chart } from 'components/Chart/Chart'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { COLORS } from 'modules/Statistics/colors'
import StatisticsLayout from 'modules/Statistics/subcomponents/Layout'
import { ChartLegend } from 'modules/Statistics/subcomponents/Layout/ChartLegend'
import styles from 'modules/Statistics/statistics.module.scss'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Statistics/Main/filters'
import FilterPanel from 'components/Filters/FilterPanel'
import omit from 'lodash/omit'
import isEqual from 'lodash/isEqual'
import { Button, Grid, Icon } from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import Switch from 'components/Switch'
import TableComponent from './subcomponents/TableComponent'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import PageHeader from 'components/PageHeader'
import useChangeChartType from 'modules/Statistics/hooks/useChangeChartType'
import GridSpinner from 'components/UI/GridSpinner'
import EmptyResults from 'components/EmptyResults'
import {
  DROP_TABLE_DATA,
  FETCH_ACCOUNTS,
  FETCH_CHART,
  FETCH_GROUPS,
  RESET_FILTERS,
  SET_FILTER,
  TOGGLE_METRIC_HIDDEN,
  UPDATE_ACCOUNT_GROUPS,
  UPDATE_GROUP,
} from 'modules/Statistics/types'

const StatisticsMain = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { fb } = useBreakpoint()
  const [cfg, setCfg] = useLocalStorage('statisticsConfig', {
    showStatisticsChart: true,
  })
  const [chartVisible, setChartVisible] = useState(cfg.showStatisticsChart)
  const { chart, filters, accounts, groups, accountsLoaded } = useSelector(
    ({ statistics }) => ({
      chart: statistics.chart,
      filters: statistics.filters,
      accounts: statistics.accounts,
      groups: statistics.groups,
      accountsLoaded: statistics.accountsLoaded,
    }),
    shallowEqual,
  )
  const i18n = useSelector(({ app }) => app.i18n)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [userHasAccounts, setUserHasAccounts] = useState(false)
  const [isDemoMode, setIsDemoMode] = useLocalStorage('isDemoMode', true)
  const [tableGroup, setTableGroup] = useLocalStorage('statisticsTableGroup', {
    tableGroupBy: 'accounts',
  })

  const [groupBy, setGroupBy] = useState(tableGroup.tableGroupBy)

  const filterManager = useFiltersManager({
    filters: filters,
    config: config(groupBy),
    additionalFilterState: {
      ...filters,
      ...isDemoMode,
    },
    setAction: SET_FILTER,
    nonClearableFilters: ['date_group_period', 'include_vat'],
    localStorageKey: `main-statistics-${groupBy}`,
  })

  const mapAccountsToOptions = useCallback(
    (data) =>
      [...data]
        .sort((a, b) => {
          const nameA = a.login.toLowerCase()
          const nameB = b.login.toLowerCase()
          if (nameA < nameB) return -1
          if (nameA > nameB) return 1
          return 0
        })
        .map(({ login, id, ...rest }) => ({ ...rest, value: id, label: login })),
    [],
  )

  const mapGroupsToOptions = useCallback(
    (data) =>
      [...data]
        .sort((a, b) => {
          const nameA = (a.name ?? '').toLowerCase()
          const nameB = (b.name ?? '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        .map(({ name, id }) => ({ value: id, label: name })),
    [],
  )

  const accountsOptions = useMemo(
    () => mapAccountsToOptions(accounts),
    [accounts, mapAccountsToOptions],
  )
  const groupsOptions = useMemo(() => mapGroupsToOptions(groups), [groups, mapGroupsToOptions])

  useEffect(() => {
    filterManager.updateFilterConfigurationByKey({
      key: 'account_ids',
      val: { options: accountsOptions },
    })
  }, [accountsOptions])

  useEffect(() => {
    filterManager.updateFilterConfigurationByKey({
      key: 'account_group_ids',
      val: { options: groupsOptions },
    })
  }, [groupsOptions])

  const refreshOptions = useCallback(() => {
    dispatch({ type: FETCH_ACCOUNTS })
    dispatch({ type: FETCH_GROUPS })
  }, [])

  useEffect(() => {
    if (isDemoMode) {
      setIsFirstLoad(false)
      setUserHasAccounts(true)
      return
    }

    refreshOptions()
  }, [isDemoMode])

  useEffect(() => {
    if (isDemoMode) return
    if (!accountsLoaded) return

    setIsFirstLoad(false)
    setUserHasAccounts(Boolean(accounts?.length))
  }, [isDemoMode, accountsLoaded, accounts])

  useEffect(() => {
    setTableGroup({ ...tableGroup, tableGroupBy: groupBy })
    const newConfig = config(groupBy)
    filterManager.reInit({ config: newConfig })
    dispatch({ type: RESET_FILTERS, payload: filterManager.getMergedConfig(newConfig) })
    // refreshOptions()
  }, [groupBy])

  const [filtersState, setAccountFiltersState] = useState(
    omit(filterManager.filters, ['include_vat']),
  )

  useEffect(() => {
    const omittedFilters = omit(filterManager.filters, ['include_vat'])
    !isEqual(filtersState, omittedFilters) && setAccountFiltersState(omittedFilters)
  }, [filterManager.filters])

  const changeChartType = useChangeChartType(chart)
  const [showedGroupOnChartId, setShowedGroupOnChartId] = useState(null)

  const toggleMetricHidden = useCallback(
    (id) => dispatch({ type: TOGGLE_METRIC_HIDDEN, payload: { id } }),
    [],
  )

  const changeChartPeriod = useCallback(
    (value) => filterManager.updateFilter({ fieldName: 'date_group_period', value }),
    [filterManager],
  )

  const switchToDemoMode = useCallback(() => setIsDemoMode(true), [setIsDemoMode])

  const handleSetGroupBy = useCallback((newGroupByValue) => {
    dispatch({ type: DROP_TABLE_DATA })
    setGroupBy(newGroupByValue)
  }, [])

  const handleAccountAttach = useCallback(
    ({ account, groupIds }) => {
      dispatch({ type: UPDATE_ACCOUNT_GROUPS, payload: { account, groupIds, groupBy } })
      refreshOptions()
    },
    [groupBy, refreshOptions],
  )

  const handleChangeGroup = useCallback(
    ({ id, newGroupData }) => {
      dispatch({ type: UPDATE_GROUP, payload: { id, newGroupData } })
      refreshOptions()
    },
    [refreshOptions],
  )

  const emptyDataLabel = useMemo(
    () => ({
      text: t('statistics::chart.empty_data.text', {
        context: isDemoMode ? 'demo' : '',
      }),
      actionButton: !isDemoMode ? (
        <Button
          variant={'primary'}
          size={'m'}
          onClick={switchToDemoMode}
        >
          {t('statistics::demo_mode_switcher')}
        </Button>
      ) : null,
    }),
    [isDemoMode, t, switchToDemoMode],
  )

  const dateGroupPeriod = useMemo(
    () => ({
      value: filterManager.filters?.date_group_period ?? 'day',
      options: filterManager.configuration.date_group_period?.options,
    }),
    [
      filterManager.filters?.date_group_period,
      filterManager.configuration.date_group_period?.options,
    ],
  )

  useEffect(() => {
    filterManager.filters &&
      dispatch({
        type: FETCH_CHART,
        payload: {
          isDemoMode,
          params: {
            ...(showedGroupOnChartId ? { account_group_ids: [showedGroupOnChartId] } : {}),
          },
        },
      })
  }, [filterManager.filters, isDemoMode, groupBy, showedGroupOnChartId])

  useEffect(() => {
    setCfg({ showStatisticsChart: chartVisible })
  }, [chartVisible])

  return (
    <>
      <Grid
        className={'margin-bottom-32 layout-tile'}
        justifyContent={!fb ? 'between' : 'start'}
        alignItems={'center'}
      >
        <PageHeader color={'Label-Primary'}>{t('common::nav_menu.statistics')}</PageHeader>

        <Switch
          label={t('statistics::demo_mode_switcher')}
          checked={isDemoMode}
          onChange={() => {
            setIsDemoMode(!isDemoMode)
          }}
          className={styles['demo-switcher']}
        />
      </Grid>

      {isFirstLoad && !isDemoMode ? (
        <GridSpinner />
      ) : userHasAccounts ? (
        <StatisticsLayout
          chartVisible={chartVisible}
          FilterPanel={
            filterManager.filters && (
              <FilterPanel
                filterManager={filterManager}
                className={styles['filter-panel']}
              />
            )
          }
          ChartComponent={
            <Chart
              chartHeaderClassName={styles['chart-header_tab-legend']}
              chartLegend={
                <ChartLegend
                  measure={chart.measure}
                  metrics={chart.metrics}
                  summary={chart.summary}
                  include_vat={filterManager.filters?.include_vat}
                  handleClick={toggleMetricHidden}
                />
              }
              i18n={i18n}
              emptyDataLabel={emptyDataLabel}
              colors={COLORS}
              className={styles['chart']}
              chartType={chart.chartType}
              items={chart.data}
              metrics={chart.metrics}
              loading={chart.loading}
              date_group_period={dateGroupPeriod}
              include_vat={filterManager.filters?.include_vat}
              summary={chart.summary}
              measure={chart.measure}
              changeChartPeriod={changeChartPeriod}
              changeChartType={changeChartType}
            />
          }
          TableComponent={
            filterManager.filters && (
              <TableComponent
                groupBy={groupBy}
                setGroupBy={handleSetGroupBy}
                showedGroupOnChartId={showedGroupOnChartId}
                setShowedGroupOnChartId={setShowedGroupOnChartId}
                chartVisible={chartVisible}
                setChartVisible={setChartVisible}
                filters={filterManager.filters}
                isDemoMode={isDemoMode}
                handleAccountAttach={handleAccountAttach}
                handleChangeGroup={handleChangeGroup}
              />
            )
          }
        />
      ) : (
        <EmptyResults
          icon={<Icon name={'statistics'} />}
          text={t('statistics::tracked_accounts_empty')}
        />
      )}
    </>
  )
}

StatisticsMain.propTypes = {}

export default StatisticsMain
